/**
 * Service for printing ZPL to a portable Zebra printer over the Web Bluetooth API (BLE).
 * Mirrors SerialScannerService: a module-level singleton whose connection survives SPA
 * navigation (paired in Configurations, used in Expeditions).
 */

// Kill switch (WET-4997): the portable printer is NOT in production use yet (no Zebra
// hardware in the field). While false, no BLE work runs anywhere in the app so the PDA's
// Bluetooth stack stays idle and can't degrade box-scanning performance. Flip to true and
// redeploy once the Zebra is deployed. Consumers must gate on this before any BLE call.
export const PORTABLE_PRINTER_ENABLED = false

// Zebra BLE "Print Service" UUIDs — candidate values, confirm on hardware (Wave 0 spike).
const SERVICE_UUID = '38eb4a80-c570-11e3-9507-0002a5d5c51b'
const WRITE_CHAR_UUID = '38eb4a82-c570-11e3-9507-0002a5d5c51b'
// Bytes per BLE write — candidate value, confirm on hardware (start at 20).
const CHUNK_SIZE = 20
// localStorage key to re-identify the paired printer across reloads.
const DEVICE_ID_KEY = 'bt_printer_device_id'
// Abort a GATT connect that hangs (e.g. a stale/absent paired device) so a pending
// connection can't keep the BT stack busy and slow the whole PDA down for the session.
const CONNECT_TIMEOUT_MS = 5000

declare global {
  interface Navigator {
    bluetooth: {
      getDevices(): Promise<Array<BluetoothDevice>>
      requestDevice(options: RequestDeviceOptions): Promise<BluetoothDevice>
    }
  }

  type RequestDeviceOptions = {
    filters?: Array<{ services?: Array<string>; namePrefix?: string }>
    optionalServices?: Array<string>
    acceptAllDevices?: boolean
  }

  type BluetoothDevice = {
    id: string
    name?: string
    gatt?: BluetoothRemoteGATTServer
    addEventListener(type: 'gattserverdisconnected', listener: () => void): void
  }

  type BluetoothRemoteGATTServer = {
    connected: boolean
    connect(): Promise<BluetoothRemoteGATTServer>
    disconnect(): void
    getPrimaryService(uuid: string): Promise<BluetoothRemoteGATTService>
  }

  type BluetoothRemoteGATTService = {
    getCharacteristic(uuid: string): Promise<BluetoothRemoteGATTCharacteristic>
  }

  type BluetoothRemoteGATTCharacteristic = {
    writeValue(value: BufferSource): Promise<void>
  }
}

type ConnectionCallback = (connected: boolean) => void

class BluetoothPrinterService {
  private static instance: BluetoothPrinterService | null = null

  public static getInstance(): BluetoothPrinterService {
    if (!BluetoothPrinterService.instance) {
      BluetoothPrinterService.instance = new BluetoothPrinterService()
    }
    return BluetoothPrinterService.instance
  }

  private device: BluetoothDevice | null = null
  private characteristic: BluetoothRemoteGATTCharacteristic | null = null
  private onConnectionChangeCbs = new Set<ConnectionCallback>()

  public isSupported = (): boolean => 'bluetooth' in navigator

  public isConnected = (): boolean => !!this.device?.gatt?.connected && !!this.characteristic

  public getDeviceName = (): string | null => this.device?.name ?? null

  // Subscribe to connection changes; returns an unsubscribe fn to call on unmount.
  public onConnectionChange = (cb: ConnectionCallback): (() => void) => {
    this.onConnectionChangeCbs.add(cb)
    return () => {
      this.onConnectionChangeCbs.delete(cb)
    }
  }

  private emitConnectionChange = (connected: boolean): void => {
    this.onConnectionChangeCbs.forEach(cb => cb(connected))
  }

  // Interactive pairing — MUST be called from a user gesture (button click).
  public connect = async (): Promise<boolean> => {
    if (!this.isSupported()) return false
    const device = await navigator.bluetooth.requestDevice({
      filters: [{ services: [SERVICE_UUID] }],
      optionalServices: [SERVICE_UUID]
    })
    return this.attach(device)
  }

  // Silent reconnect using previously granted devices (no prompt). Best-effort.
  public reconnect = async (): Promise<boolean> => {
    if (!this.isSupported() || !('getDevices' in navigator.bluetooth)) return false
    const savedId = localStorage.getItem(DEVICE_ID_KEY)
    if (!savedId) return false
    const devices = await navigator.bluetooth.getDevices()
    const device = devices.find(d => d.id === savedId)
    if (!device) return false
    return this.attach(device)
  }

  // Connects to GATT with a timeout; on timeout it cancels the pending connection so a
  // stale/absent device can't leave the BT stack hanging for the whole session.
  private connectWithTimeout = (device: BluetoothDevice): Promise<BluetoothRemoteGATTServer> => {
    const gatt = device.gatt!
    let timer: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => {
        try {
          gatt.disconnect()
        } catch {
          // The connection may not be open yet; nothing to tear down.
        }
        reject(new Error('Bluetooth connect timed out'))
      }, CONNECT_TIMEOUT_MS)
    })
    return Promise.race([gatt.connect(), timeout]).finally(() => clearTimeout(timer))
  }

  private attach = async (device: BluetoothDevice): Promise<boolean> => {
    try {
      const server = await this.connectWithTimeout(device)
      const service = await server.getPrimaryService(SERVICE_UUID)
      this.characteristic = await service.getCharacteristic(WRITE_CHAR_UUID)
      this.device = device
      localStorage.setItem(DEVICE_ID_KEY, device.id)
      device.addEventListener('gattserverdisconnected', this.handleDisconnected)
      this.emitConnectionChange(true)
      return true
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Bluetooth printer connect failed:', error)
      this.characteristic = null
      this.device = null
      this.emitConnectionChange(false)
      return false
    }
  }

  private handleDisconnected = (): void => {
    this.characteristic = null
    this.emitConnectionChange(false)
  }

  public disconnect = async (): Promise<void> => {
    if (this.device?.gatt?.connected) {
      this.device.gatt.disconnect()
    }
    this.characteristic = null
    this.device = null
    this.emitConnectionChange(false)
  }

  // Sends raw ZPL, chunked to the BLE MTU. Throws if not connected.
  public print = async (zpl: string): Promise<void> => {
    if (!this.characteristic) {
      throw new Error('Bluetooth printer not connected')
    }
    const bytes = new TextEncoder().encode(zpl)
    for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
      await this.characteristic.writeValue(bytes.slice(i, i + CHUNK_SIZE))
    }
  }
}

export const bluetoothPrinterService = BluetoothPrinterService.getInstance()
